import { FileUploadFileMimeType } from '@chakra-ui/react';
import { createApi } from '@reduxjs/toolkit/query/react';
import { RfiSubmittalsType } from '@types';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';
import { keepUnusedDataFor } from '../constants/appActions';

export type args = {
  postRfiLogs: ApiType<RfiSubmittalsType.RfiLog.Post>['args'];
  getAllRfiLogs: ApiType<RfiSubmittalsType.RfiLog.Get>['args'];
  getRfiLog: ApiType<RfiSubmittalsType.RfiLog.GetRfiLog>['args'];
  patchRfiLog: ApiType<RfiSubmittalsType.RfiLog.Patch>['args'];
  postRfiLogsExportLog: ApiType<RfiSubmittalsType.RfiLog.PostExportLog>['args'];
  postRfiLogAttachments: ApiType<RfiSubmittalsType.RfiLog.PostRfiLogAttachments>['args'];
  deleteRfiLogAttachment: ApiType<RfiSubmittalsType.RfiLog.DeleteAttachment>['args'];
  deleteRfiLog: { params: { rfi_log_id: number } };
  getRfiReturnAttachments: ApiType<RfiSubmittalsType.RfiLog.GetRfiReturnAttachments>['args'];
  postRfiReturnAttachment: ApiType<RfiSubmittalsType.RfiLog.PostRfiReturnAttachment>['args'];
  deleteRfiReturnAttachment: ApiType<RfiSubmittalsType.RfiLog.DeleteRfiReturnAttachment>['args'];
};
export type responses = {
  postRfiLogs: ApiType<RfiSubmittalsType.RfiLog.Post>['response'];
  getAllRfiLogs: ApiType<RfiSubmittalsType.RfiLog.Get>['response'];
  getRfiLog: ApiType<RfiSubmittalsType.RfiLog.GetRfiLog>['response'];
  patchRfiLog: ApiType<RfiSubmittalsType.RfiLog.Patch>['response'];
  postRfiLogsExportLog: Blob;
  postRfiLogAttachments: ApiType<RfiSubmittalsType.RfiLog.PostRfiLogAttachments>['response'];
  deleteRfiLogAttachment: void;
  deleteRfiLog: { params: { rfi_log_id: number } };
  getRfiReturnAttachments: ApiType<RfiSubmittalsType.RfiLog.GetRfiReturnAttachments>['response'];
  postRfiReturnAttachment: ApiType<RfiSubmittalsType.RfiLog.PostRfiReturnAttachment>['response'];
  deleteRfiReturnAttachment: void;
};

export const rfiApi = createApi({
  reducerPath: 'rfiApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Rfis'],

  endpoints: (builder) => ({
    // RFI V1.0 - Start of APIS

    // rfi
    postRfi: builder.mutation<
      RfiSubmittalsType.Rfi.Get,
      RfiSubmittalsType.Rfi.Post
    >({
      query: (body) => ({
        url: '/rfi/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: [{ type: 'Rfis' }],
    }),

    getAllRfis: builder.query<RfiSubmittalsType.Rfi.Get[], void>({
      query: () => ({
        url: '/rfi/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (result) =>
        result ? [{ type: 'Rfis', id: 'rfisAll' }] : [],
    }),

    getRfiById: builder.query<
      Omit<RfiSubmittalsType.Rfi.Get, 'id'>,
      { rfi_id: number }
    >({
      query: ({ rfi_id }) => ({
        url: `/rfi/${rfi_id}/`,
        method: 'GET',

        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 404) &&
          !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _rfi_id) =>
        _result ? [{ type: 'Rfis', id: `rfi-${_rfi_id}` }] : [],
    }),

    patchRfiById: builder.mutation<
      RfiSubmittalsType.Rfi.Get,
      {
        rfi_id: number;
        body: RfiSubmittalsType.Rfi.Patch;
      }
    >({
      query: ({ rfi_id, body }) => ({
        url: `/rfi/${rfi_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Rfis' }] : [],
    }),

    deleteRfiById: builder.mutation<void, { rfi_id: number }>({
      query: ({ rfi_id }) => ({
        url: `/rfi/${rfi_id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, arg) =>
        _result ? [{ type: 'Rfis', id: arg.rfi_id }] : [],
    }),

    postRfiAttachmentS3PreSign: builder.mutation<
      {
        presigned_urls: {
          filename: string;
          upload_url: string;
          file_url: string;
        }[];
      },
      {
        files: {
          filename: string;
          content_type: FileUploadFileMimeType;
          size?: number;
        }[];
      }
    >({
      query: (body) => ({
        url: '/rfi/attachments/upload/presign/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    putRfiAttachmentUploadToS3: builder.mutation<
      {
        presigned_urls: {
          filename: string;
          upload_url: string;
          file_url: string;
        }[];
      },
      {
        file: File;
        content_type: FileUploadFileMimeType;
        s3_url: string;
      }
    >({
      query: ({ file, content_type, s3_url }) => ({
        url: s3_url,
        method: 'PUT',
        headers: { 'Content-Type': content_type },
        body: file,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    //   status
    patchRfiMessageById: builder.mutation<
      Pick<RfiSubmittalsType.Rfi.Get, 'status'>,
      {
        rfi_id: number;
        body: Pick<RfiSubmittalsType.Rfi.Get, 'status'>;
      }
    >({
      query: ({ rfi_id, body }) => ({
        url: `/rfi/${rfi_id}/status`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, arg) =>
        _result ? [{ type: 'Rfis', id: arg.rfi_id }] : [],
    }),

    //   ball in court
    postBallInCourtByRfiId: builder.mutation<
      { user_id: number },
      {
        rfi_id: number;
        body: {
          user_id: number;
        };
      }
    >({
      query: ({ rfi_id, body }) => ({
        url: `/rfi/${rfi_id}/ball-in-court/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: [{ type: 'Rfis' }],
    }),
    deleteBallInCourtByRfiId: builder.mutation<void, { rfi_id: number }>({
      query: ({ rfi_id }) => ({
        url: `/rfi/${rfi_id}/ball-in-court/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error) => (_result ? [{ type: 'Rfis' }] : []),
    }),

    //   messages
    postMessage: builder.mutation<
      RfiSubmittalsType.Rfi.RfiMessage,
      {
        rfi_id: number;
        body: Pick<
          RfiSubmittalsType.Rfi.RfiMessage,
          'message' | 'attachments'
        > & {
          bic_user: number;
        };
      }
    >({
      query: ({ rfi_id, body }) => ({
        url: `/rfi/${rfi_id}/messages/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error) =>
        _result ? [{ type: 'Rfis', id: `rfi-messages-${_result.rfi_id}` }] : [],
    }),

    getMessages: builder.query<
      RfiSubmittalsType.Rfi.RfiMessage[],
      { rfi_id: number }
    >({
      query: ({ rfi_id }) => ({
        url: `/rfi/${rfi_id}/messages/`,
        method: 'GET',

        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 404) &&
          !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Rfis', id: `rfi-messages-${_arg.rfi_id}` }] : [],
    }),

    getMessageById: builder.query<
      RfiSubmittalsType.Rfi.RfiMessage,
      { rfi_id: number; message_id: number }
    >({
      query: ({ rfi_id, message_id }) => ({
        url: `/rfi/${rfi_id}/messages/${message_id}/`,
        method: 'GET',

        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 404) &&
          !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Rfis', id: `rfi-messages-${_arg.rfi_id}` }] : [],
    }),

    patchMessageById: builder.mutation<
      RfiSubmittalsType.Rfi.RfiMessage,
      {
        rfi_id: number;
        message_id: number;
        body: Pick<RfiSubmittalsType.Rfi.RfiMessage, 'message'>;
      }
    >({
      query: ({ rfi_id, message_id, body }) => ({
        url: `/rfi/${rfi_id}/messages/${message_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Rfis', id: `rfi-messages-${_arg.rfi_id}` }] : [],
    }),

    deleteMessageById: builder.mutation<
      void,
      { rfi_id: number; message_id: number }
    >({
      query: ({ rfi_id, message_id }) => ({
        url: `/rfi/${rfi_id}/messages/${message_id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result ? [{ type: 'Rfis', id: `rfi-messages-${_arg.rfi_id}` }] : [],
    }),

    postMessageFinalAnswer: builder.mutation<
      void,
      {
        rfi_id: number;
        message_id: number;
      }
    >({
      query: ({ rfi_id, message_id }) => ({
        url: `/rfi/${rfi_id}/messages/${message_id}/final_answer/`,
        method: 'POST',
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Rfis', id: `rfi-messages-${_arg.rfi_id}` },
      ],
    }),

    getRfiReturnAttachments: builder.query<
    responses['getRfiReturnAttachments'],
    args['getRfiReturnAttachments']
  >({
    query: ({ params }) => ({
      url: `/rfi/rfi-log/${params.rfi_log_id}/return-attachments/`,
      method: 'GET',
      validateStatus: (response, result) =>
        response.status === 200 && !result.isError,
    }),
    keepUnusedDataFor,
    providesTags: (_result, _error, _arg) => [
      { type: 'Rfis', id: `rfi-log-${_arg.params.rfi_log_id}` },
      { type: 'Rfis', id: `rfi-log-return-attachments-${_arg.params.rfi_log_id}` },
    ],
  }),

    postRfiReturnAttachment: builder.mutation<
      responses['postRfiReturnAttachment'],
      args['postRfiReturnAttachment']
    >({
      query: ({ params, body }) => ({
        url: `/rfi/rfi-log/${params.rfi_log_id}/return-attachments/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 201) && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Rfis', id: `rfi-log-${_arg.params.rfi_log_id}` },
        { type: 'Rfis', id: `rfi-log-return-attachments-${_arg.params.rfi_log_id}` },
      ],
    }),

    deleteRfiReturnAttachment: builder.mutation<
  responses['deleteRfiReturnAttachment'],
  args['deleteRfiReturnAttachment']
>({
  query: ({ params }) => ({
    url: `/rfi/rfi-log/return-attachments/${params.return_id}/`,
    method: 'DELETE',
    validateStatus: (response) => response.status === 204,
  }),
  invalidatesTags: (_result, _error, _arg) => [
    { type: 'Rfis', id: 'rfi-logs' },
    { type: 'Rfis', id: `rfi-log-${_arg.params.rfi_log_id}` },
    { type: 'Rfis', id: `rfi-log-return-attachments-${_arg.params.rfi_log_id}` },
  ],
}),

    deleteMessageFinalAnswer: builder.mutation<
      void,
      {
        rfi_id: number;
        message_id: number;
      }
    >({
      query: ({ rfi_id, message_id }) => ({
        url: `/rfi/${rfi_id}/messages/${message_id}/final-answer/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: [{ type: 'Rfis' }],
    }),

    //   users
    postUser: builder.mutation<
      Pick<RfiSubmittalsType.Rfi.Get, 'assigned_users'> & { id: number }[],
      {
        rfi_id: number;
        body: Pick<RfiSubmittalsType.Rfi.Get, 'assigned_users'>;
      }
    >({
      query: ({ rfi_id, body }) => ({
        url: `/rfi/${rfi_id}/users/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: [{ type: 'Rfis' }],
    }),

    getUser: builder.query<
      Pick<RfiSubmittalsType.Rfi.Get, 'assigned_users'> & {
        id: number;
      },
      { rfi_id: number }
    >({
      query: ({ rfi_id }) => ({
        url: `/rfi/${rfi_id}/users/`,
        method: 'GET',
        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 404) &&
          !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _rfi_id) =>
        _result ? [{ type: 'Rfis', id: `rfi-users-${_rfi_id}` }] : [],
    }),

    patchUser: builder.mutation<
      Pick<RfiSubmittalsType.Rfi.Get, 'assigned_users'> & {
        id: number;
      },
      {
        rfi_id: number;
        user_id: number;
        body: Pick<RfiSubmittalsType.Rfi.Get, 'assigned_users'>;
      }
    >({
      query: ({ rfi_id, user_id, body }) => ({
        url: `/rfi/${rfi_id}/users/${user_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, arg) =>
        _result ? [{ type: 'Rfis', id: `rfi-users-${arg.rfi_id}` }] : [],
    }),

    deleteUser: builder.mutation<void, { rfi_id: number; user_id: number }>({
      query: ({ rfi_id, user_id }) => ({
        url: `/rfi/${rfi_id}/users/${user_id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, arg) =>
        _result ? [{ type: 'Rfis', id: `rfi-users-${arg.rfi_id}` }] : [],
    }),

    // RFI V1.0 - End of APIS

    // RFI V2.0 - Start of APIS

    postRfiLog: builder.mutation<responses['postRfiLogs'], args['postRfiLogs']>(
      {
        query: ({ body }) => ({
          url: `/rfi/rfi-log/`,
          method: 'POST',
          body,
          validateStatus: (response, result) =>
            response.status === 201 && !result.isError,
        }),
        invalidatesTags: (_result, _error) =>
          _result ? [{ type: 'Rfis', id: 'rfi-logs' }] : [],
      },
    ),

    getAllRfiLogs: builder.query<
      responses['getAllRfiLogs'],
      args['getAllRfiLogs']
    >({
      query: ({ query }) => ({
        url: `/rfi/rfi-log/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error) => [{ type: 'Rfis', id: 'rfi-logs' }],
    }),

    getRfiLog: builder.query<responses['getRfiLog'], args['getRfiLog']>({
      query: ({ params }) => ({
        url: `/rfi/rfi-log/${params.rfi_log_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, id) =>
        _result
          ? [
              { type: 'Rfis', id: 'rfi-logs' },
              {
                type: 'Rfis',
                id: `rfi-log-${id}`,
              },
            ]
          : [],
    }),

    patchRfiLog: builder.mutation<
      responses['patchRfiLog'],
      args['patchRfiLog']
    >({
      query: ({ body, params }) => ({
        url: `/rfi/rfi-log/${params.rfi_log_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Rfis', id: 'rfi-logs' },
        { type: 'Rfis', id: `rfi-log-${_arg.params.rfi_log_id}` },
      ],
    }),

    getRfiLogActivity: builder.query<any, number>({
      query: (id) => ({
        url: `/rfi-log/${id}/activity/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, id) =>
        _result
          ? [
              { type: 'Rfis', id: 'rfi-logs' },
              {
                type: 'Rfis',
                id: `rfi-log-activity-${id}`,
              },
            ]
          : [],
    }),

    postRfiLogAttachments: builder.mutation<
      responses['postRfiLogAttachments'],
      args['postRfiLogAttachments']
    >({
      query: ({ body, params }) => ({
        url: `/rfi/rfi-log/${params.rfi_log_id}/attachments/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Rfis', id: 'rfi-logs' },
        { type: 'Rfis', id: `rfi-log-${_arg.params.rfi_log_id}` },
      ],
    }),

    postRfiLogExportPdf: builder.mutation<
      Blob,
      {
        params: { rfi_log_id: number };
        body: {
          to_name?: string;
          spec_ref?: string;
          date?: string;
          date_required?: string;
          description?: string;
          category: {
            information_not_on_contract: number;
            interpretation_of_contract: number;
            conflict_in_contract: number;
            coordination_problem: number;
          };
          contract_drawing_ref: string;
          shop_drawing_ref: string;
          distribution_list: string[];
        };
      }
    >({
      query: ({ params, body }) => ({
        url: `/rfi/rfi-log/${params.rfi_log_id}/export/`,
        method: 'POST',
        body,
        //returns blob
        responseHandler: (response: Response) => response.blob(),
        validateStatus: (response) => response.status === 200,
      }),
    }),

    postRfiLogsExportLog: builder.mutation<
      responses['postRfiLogsExportLog'],
      args['postRfiLogsExportLog']
    >({
      query: ({ body }) => ({
        url: `/rfi/rfi-log/export-log/`,
        method: 'POST',
        body,
        responseHandler: async (response: Response) => {
          if (response.ok) return response.blob();
          const contentType = response.headers.get('content-type') ?? '';
          return contentType.includes('application/json')
            ? response.json()
            : { detail: await response.text() };
        },
        validateStatus: (response) => response.status === 200,
      }),
    }),

    deleteRfiLog: builder.mutation<
      responses['deleteRfiLog'],
      args['deleteRfiLog']
    >({
      query: ({ params }) => ({
        url: `/rfi/rfi-log/${params.rfi_log_id}/delete/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Rfis', id: 'rfi-logs' },
        { type: 'Rfis', id: `rfi-log-${_arg.params.rfi_log_id}` },
      ],
    }),

    deleteRfiLogAttachment: builder.mutation<
      responses['deleteRfiLogAttachment'],
      args['deleteRfiLogAttachment']
    >({
      query: ({ params }) => ({
        url: `/rfi/rfi-log/${params.rfi_log_id}/attachments/${params.attachment_id}/`,
        method: 'DELETE',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Rfis', id: 'rfi-logs' },
        { type: 'Rfis', id: `rfi-log-${_arg.params.rfi_log_id}` },
      ],
    }),

    // RFI V2.0 - End of APIS
  }),
});

export const {
  // RFI V1.0 - Start of APIS
  // rfi
  usePostRfiMutation,
  useGetAllRfisQuery,
  useGetRfiByIdQuery,
  usePatchRfiByIdMutation,
  useDeleteRfiByIdMutation,
  usePostRfiAttachmentS3PreSignMutation,
  usePutRfiAttachmentUploadToS3Mutation,

  //   status

  //ball in court

  //   messages
  usePostMessageMutation,
  useGetMessageByIdQuery,
  usePatchMessageByIdMutation,
  useDeleteMessageByIdMutation,
  useGetMessagesQuery,
  // RFI V1.0 - End of APIS

  // RFI V2.0 - Start of APIS
  useGetRfiLogQuery,
  usePostRfiLogMutation,
  useGetAllRfiLogsQuery,
  usePatchRfiLogMutation,
  useGetRfiLogActivityQuery,
  usePostRfiLogAttachmentsMutation,
  useDeleteRfiLogAttachmentMutation,
  usePostRfiLogExportPdfMutation,
  usePostRfiLogsExportLogMutation,
  useDeleteRfiLogMutation,
  useGetRfiReturnAttachmentsQuery,
  usePostRfiReturnAttachmentMutation,
  useDeleteRfiReturnAttachmentMutation,
  // RFI V2.0 - End of APIS
} = rfiApi;

// getProjects: builder.query<any[], void>({
//   query: () => '/projects/',
//   providesTags: (result) =>
//     result
//       ? [
//           ...result.map(({ id }) => ({ type: 'Projects' as const, id })),
//           { type: 'Projects', id: 'LIST' },
//         ]
//       : [{ type: 'Projects', id: 'LIST' }],

export namespace RfiApi {
  export const Api = rfiApi;
  export type Args = args;
  export type Responses = responses;
}
